// The fetch wrapper: same-origin `/api/v1` (Vite proxies it to the API in dev), the session cookie,
// the CSRF header on unsafe methods, envelope unwrapping, typed errors, and ETag-aware GETs.

export const API_BASE = "/api/v1";

export interface ValidationDetail {
	path?: string;
	rule?: string;
	message: string;
	suggested_fix?: unknown;
}

/** A failed request: `code` is the API's machine-readable `error` (VALIDATION, NOT_FOUND, UNDO_CONFLICT…). */
export class ApiError extends Error {
	status: number;
	code: string;
	details: unknown;
	retryAfterS: number | null;

	constructor(status: number, code: string, message: string, details?: unknown, retryAfterS: number | null = null) {
		super(message);
		this.status = status;
		this.code = code;
		this.details = details;
		this.retryAfterS = retryAfterS;
	}

	/** The detail messages (validation), or the message. For toasts. */
	get messages(): string[] {
		if (Array.isArray(this.details)) {
			const ms = this.details.map((d) => (d && typeof d === "object" && "message" in d ? String((d as ValidationDetail).message) : "")).filter(Boolean);
			if (ms.length) return [...new Set(ms)];
		}
		return [this.message];
	}
}

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null) {
	csrfToken = token;
}

/** Called on every 401, so the app can drop back to the sign-in screen. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
	onUnauthorized = fn;
}

/** Whether the last request reached the API. The app shows a banner while it doesn't. */
type ConnectionListener = (online: boolean) => void;
const connectionListeners = new Set<ConnectionListener>();
let online = true;
function setOnline(v: boolean) {
	if (v === online) return;
	online = v;
	for (const fn of connectionListeners) fn(v);
}
export function onConnectionChange(fn: ConnectionListener): () => void {
	connectionListeners.add(fn);
	return () => connectionListeners.delete(fn);
}
export function isOnline() {
	return online;
}

const etags = new Map<string, { etag: string; data: unknown }>();
const ETAG_MAX = 200;

function remember(path: string, etag: string, data: unknown) {
	etags.delete(path);
	etags.set(path, { etag, data });
	if (etags.size > ETAG_MAX) etags.delete(etags.keys().next().value!);
}

export function clearHttpCache() {
	etags.clear();
}

export interface RequestOptions {
	headers?: Record<string, string>;
	signal?: AbortSignal;
	/** Send `If-None-Match` from the last response to this path and reuse its body on a 304. */
	etag?: boolean;
}

export async function request<T>(method: string, path: string, body?: unknown, opts: RequestOptions = {}): Promise<T> {
	const url = path.startsWith("/") ? `${API_BASE}${path}` : `${API_BASE}/${path}`;
	const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
	if (body !== undefined) headers["Content-Type"] = "application/json";
	const unsafe = !["GET", "HEAD", "OPTIONS"].includes(method);
	if (unsafe && csrfToken) headers["x-csrf-token"] = csrfToken;
	const cached = method === "GET" && opts.etag ? etags.get(path) : undefined;
	if (cached) headers["If-None-Match"] = cached.etag;

	let res: Response;
	try {
		res = await fetch(url, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: "include", signal: opts.signal });
	} catch (e) {
		if ((e as Error).name === "AbortError") throw e;
		setOnline(false);
		throw new ApiError(0, "NETWORK", "Can’t reach the ModelCurve API. Is it running?");
	}

	// A proxy's 502/504 means the API itself is down (Vite in development, the load balancer in production).
	setOnline(res.status !== 502 && res.status !== 504);
	if (res.status === 304 && cached) return cached.data as T;
	if (res.status === 204) return undefined as T;

	let payload: unknown = null;
	const text = await res.text();
	if (text) {
		try {
			payload = JSON.parse(text);
		} catch {
			payload = null;
		}
	}
	const env = payload as { success?: boolean; data?: unknown; error?: string; message?: string; details?: unknown } | null;
	if (!res.ok || !env || env.success === false) {
		const retry = Number(res.headers.get("Retry-After"));
		const err = new ApiError(
			res.status,
			env?.error ?? (res.status === 502 || res.status === 504 ? "NETWORK" : `HTTP_${res.status}`),
			env?.message ?? (res.status >= 502 ? "Can’t reach the ModelCurve API. Is it running?" : `Request failed (${res.status})`),
			env?.details,
			Number.isFinite(retry) && retry > 0 ? retry : null,
		);
		if (res.status === 401 && onUnauthorized) onUnauthorized();
		throw err;
	}
	const data = env.data as T;
	if (method === "GET" && opts.etag) {
		const etag = res.headers.get("ETag");
		if (etag) remember(path, etag, data);
	}
	return data;
}

export const api = {
	get: <T>(path: string, opts?: RequestOptions) => request<T>("GET", path, undefined, { etag: true, ...opts }),
	post: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("POST", path, body ?? {}, opts),
	put: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PUT", path, body ?? {}, opts),
	patch: <T>(path: string, body?: unknown, opts?: RequestOptions) => request<T>("PATCH", path, body ?? {}, opts),
	del: <T>(path: string, opts?: RequestOptions) => request<T>("DELETE", path, undefined, opts),
};

/** `?a=1&b=2`, skipping empty values. */
export function qs(params: Record<string, string | number | null | undefined>): string {
	const parts = Object.entries(params)
		.filter(([, v]) => v !== null && v !== undefined && v !== "")
		.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
	return parts.length ? `?${parts.join("&")}` : "";
}
